<template>
  <div class="row">
    <div class="col-lg-6">
      <div class="gutter-bottom">
        <selectlist
          :options="vehicleUseTypes"
          name="use_id"
          v-model="use_id"
          v-on:input="updateReason"
          helper="Please select what the vehicle was being used for at the time of the accident"
          placeholder="Please select"
          label="Vehicle use"
        ></selectlist>

        <inputbox
          name="purpose_of_journey"
          label="Purpose of Journey"
          v-model="purpose_of_journey"
          v-on:input="$emit('set-purpose', purpose_of_journey)"
          helper="Please state the purpose of the journey"
        ></inputbox>
      </div>
      <div class="gutter-bottom">
        <inputbox
          name="from"
          label="From"
          v-model="from"
          helper="Where was the vehicle travelling from when the accident occurred?"
        ></inputbox>
        <inputbox
          name="to"
          label="To"
          v-model="to"
          helper="Where was the vehicle travelling to when the accident occurred?"
        ></inputbox>
        <div class="form-group">
          <button
            class="btn btn-block btn-primary"
            v-on:click.prevent="getRoute"
          >
            Calculate Route
          </button>
        </div>
      </div>
    </div>

    <div class="map-section col-lg-6">
      <div id="route"></div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      from: null,
      to: null,
      center: {
        lat: 51.507,
        lng: 0.127
      },
      start: {},
      end: {},
      use_id: null,
      purpose_of_journey: null,
      isMapLoaded: false
    };
  },

  computed: {
    vehicleUseTypes() {
      return this.$store.getters['VehicleUseTypes/all'];
    }
  },

  mounted() {
    this.setMap();
  },

  methods: {
    /**
     * Get the currently set journey data
     *
     * @param data the response returned from ajax call in parent component
     */
    getJourney(data) {
      this.from = data.start_location;
      this.to = data.end_location;
      this.use_id = data.use_id;
      this.purpose_of_journey = data.purpose_of_journey;
      this.updateReason();

      if (!this.isMapLoaded) {
        const unwatch = this.$watch('isMapLoaded', loaded => {
          if (loaded) {
            this.getRoute();
            unwatch();
          }
        });
      } else {
        this.getRoute();
      }
    },

    updateReason() {
      this.$emit('set-reason', this.use_id);
    },

    setMap() {
      window.GoogleMapsLoader.load(google => {
        let london = new google.maps.LatLng(51.507, 0.127);

        let options = {
          center: london,
          zoom: 14
        };

        window.journeymap = new google.maps.Map(
          document.getElementById('route'),
          options
        );
        this.isMapLoaded = true;
      });
    },

    getRoute() {
      if (!this.isMapLoaded || !this.from || !this.to) {
        return;
      }

      window.google.maps
        .importLibrary('routes')
        .then(({ Route }) =>
          Route.computeRoutes({
            origin: this.from,
            destination: this.to,
            travelMode: 'DRIVING',
            routingPreference: 'TRAFFIC_AWARE',
            computeAlternativeRoutes: false,
            language: 'en-GB',
            fields: ['path', 'legs']
          })
        )
        .then(response => {
          if (response.routes && response.routes.length) {
            this.displayRoute(response.routes[0]);
            return;
          }

          this.showError(
            'Sorry, we could not find a journey. Please be more specific with where the client was traveling from and to.'
          );
        })
        .catch(error => this.handleRouteError(error));
    },

    displayRoute(route) {
      const leg = route.legs[0];

      this.start.location = this.from;
      this.start.latitude = leg.startLocation.lat;
      this.start.longitude = leg.startLocation.lng;
      this.end.location = this.to;
      this.end.latitude = leg.endLocation.lat;
      this.end.longitude = leg.endLocation.lng;

      this.clearMapElements();
      this.drawRoute(route.path);
      this.addMarkers();
      this.fitMapBounds(route.path);
      this.emitRouteData();
    },

    clearMapElements() {
      if (window.currentPolyline) {
        window.currentPolyline.setMap(null);
      }

      if (window.startMarker) {
        window.startMarker.setMap(null);
      }

      if (window.endMarker) {
        window.endMarker.setMap(null);
      }
    },

    drawRoute(path) {
      window.currentPolyline = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#4285F4',
        strokeOpacity: 1,
        strokeWeight: 4
      });

      window.currentPolyline.setMap(window.journeymap);
    },

    addMarkers() {
      window.startMarker = new window.google.maps.Marker({
        position: {
          lat: this.start.latitude,
          lng: this.start.longitude
        },
        map: window.journeymap,
        label: 'A',
        title: 'Start'
      });

      window.endMarker = new window.google.maps.Marker({
        position: {
          lat: this.end.latitude,
          lng: this.end.longitude
        },
        map: window.journeymap,
        label: 'B',
        title: 'End'
      });
    },

    fitMapBounds(path) {
      const bounds = new window.google.maps.LatLngBounds();

      path.forEach(point => bounds.extend(point));
      window.journeymap.fitBounds(bounds);
    },

    emitRouteData() {
      this.$emit('set-route', {
        start_location: this.start.location,
        start_latitude: this.start.latitude,
        start_longitude: this.start.longitude,
        end_location: this.end.location,
        end_latitude: this.end.latitude,
        end_longitude: this.end.longitude,
        use_id: this.use_id
      });
    },

    handleRouteError(error) {
      const status = error.code || error.status || '';
      const errorMessage = error.message || '';

      if (
        status === 'PERMISSION_DENIED' ||
        errorMessage.indexOf('denied') !== -1
      ) {
        this.showError(
          'The Routes API request was denied. Please ensure the Routes API is enabled in your Google Cloud Console and your API key has the necessary permissions.'
        );
        return;
      }

      if (status === 'NOT_FOUND') {
        this.showError(
          'One or more locations could not be found. Please check the addresses and try again.'
        );
        return;
      }

      if (status === 'RESOURCE_EXHAUSTED') {
        this.showError('API quota exceeded. Please try again in a moment.');
        return;
      }

      this.showError(
        'Unable to calculate route: ' +
          errorMessage +
          '. Please try again or contact support.'
      );
    },

    showError(message) {
      window.swal('Error', message, 'error');
    }
  }
};
</script>
